import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

const INDEX_SEARCH_PARAM_KEY = 'index';

const getIndexSearchParam = (history) => {
  const params = new URLSearchParams(history.location.search);
  return parseInt(params.get(INDEX_SEARCH_PARAM_KEY), 10);
};

const removeIndexSearchParam = (history) => {
  const params = new URLSearchParams(history.location.search);
  params.delete(INDEX_SEARCH_PARAM_KEY);
  history.replace({ search: params.toString() });
};

const setIndexSearchParam = (history, nextIndex) => {
  const params = new URLSearchParams(history.location.search);
  params.set(INDEX_SEARCH_PARAM_KEY, nextIndex);
  history.replace({ search: params.toString() });
};

const updateIndexSearchParam = (history, nextIndex) => {
  const params = new URLSearchParams(history.location.search);
  params.set(INDEX_SEARCH_PARAM_KEY, nextIndex);
  history.push({ search: params.toString() });
};

/**
 * useCollectionStepper is a custom hook that manages stepping through a collection.
 * It is now fully driven by the URL Search Parameter (index).
 *
 * @param collection - A Facet Rest Collection or any object with a .resources().list() method.
 * @returns An object containing the current resource, index, navigation functions, and state.
 */
function useCollectionStepper(collection) {
  const history = useHistory();
  const collArray = collection.resources().list() || [];
  const total = collArray.length;
  const indexParam = getIndexSearchParam(history);

  // Calculate the current 0-based index, relying only on the URL/History state
  const arrayIndex = useMemo(() => {
    const calculatedIndex = Number.isNaN(indexParam) ? 0 : indexParam;

    // Ensure index is within bounds [0, total-1]
    if (calculatedIndex >= total) {
      return 0;
    }
    if (calculatedIndex < 0) {
      return 0;
    }
    return calculatedIndex;
  }, [indexParam, total]);

  // If the index in the URL is invalid (e.g., NaN or out of bounds), clean it up on mount.
  useEffect(() => {
    // If the index we calculated (arrayIndex) is different from what was in the URL (indexParam),
    // it means the URL was invalid (NaN or out of bounds) and needs to be cleaned up.
    if (indexParam !== arrayIndex) {
      setIndexSearchParam(history, arrayIndex);
    }
  }, [arrayIndex, indexParam, history]);

  useEffect(() => {
    // Remove the index query parameter from the URL when the component dismounts.
    return () => {
      const params = new URLSearchParams(history.location.search);
      if (params.has(INDEX_SEARCH_PARAM_KEY)) {
        params.delete(INDEX_SEARCH_PARAM_KEY);
        history.replace({ search: params.toString() });
      }
    };
  }, [history]);

  // A handler to finalize the stepper
  const finished = () => {
    removeIndexSearchParam(history);
  };

  // Navigation functions
  const next = () => {
    window.scrollTo(0, 0);
    if (arrayIndex < total - 1) {
      // Direct update to the URL
      updateIndexSearchParam(history, arrayIndex + 1);
    }
  };

  const previous = () => {
    window.scrollTo(0, 0);
    if (arrayIndex > 0) {
      // Direct update to the URL
      updateIndexSearchParam(history, arrayIndex - 1);
    }
  };

  const setIndex = (newIndex) => {
    if (newIndex >= 0 && newIndex < total) {
      updateIndexSearchParam(history, newIndex);
    }
  };

  // Function to update a resource at a specific index
  const setResourceAtIndex = (resourceToSet, indexToSet) => {
    collection.resources().replaceAtIndex(indexToSet, resourceToSet);
  };


  return {
    resource: collArray[arrayIndex],
    index: arrayIndex + 1, // One-based index for display
    total,
    finished,
    next,
    previous,
    isStart: arrayIndex === 0,
    isEnd: arrayIndex === total - 1,
    setResourceAtIndex,
    setIndex,
  };
}

export { useCollectionStepper, useCollectionStepper as useReppie };
